/**
 * useDiagnosticsEvents — fetches and paginates diagnostics.events.list.
 *
 * @param {{
 *   enabled: boolean,
 *   severityMin?: string,
 *   scopes?: string[],
 *   limit?: number,
 * }} options
 *
 * @returns {{
 *   events: NormalizedEvent[],
 *   hasMore: boolean,
 *   nextSinceId: number,
 *   isLoading: boolean,
 *   error: string|null,
 *   loadMore: (currentNextSinceId: number) => void,
 *   refresh: () => void,
 * }}
 *
 * NormalizedEvent (camelCase):
 * {
 *   id: number,
 *   severity: string,
 *   scope: string,
 *   code: string,
 *   message: string,
 *   occurredAt: string,
 *   repeatCount: number,
 *   payload: object|null,
 *   source: string|null,
 *   requestId: string|null,
 *   jobId: string|null,
 *   fingerprint: string|null,
 *   payloadTruncated: boolean,
 * }
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { BridgeClient } from '../../../app/service/bridge/BridgeClient';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

const EMPTY_SCOPES = Object.freeze([]);

function normalizeEvent(raw) {
  return {
    id: raw.id,
    severity: raw.severity || 'info',
    scope: raw.scope || '',
    code: raw.code || '',
    message: raw.message || '',
    occurredAt: raw.occurred_at || null,
    repeatCount: raw.repeat_count ?? 0,
    payload: raw.payload ?? null,
    source: raw.source ?? null,
    requestId: raw.request_id ?? null,
    jobId: raw.job_id ?? null,
    fingerprint: raw.fingerprint ?? null,
    payloadTruncated: !!raw.payload_truncated,
  };
}

export function useDiagnosticsEvents({
  enabled,
  severityMin = 'warning',
  scopes = EMPTY_SCOPES,
  limit = 100,
}) {
  const t = useLocalizedMessage();
  const [events, setEvents] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextSinceId, setNextSinceId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const bridgeRef = useRef(null);

  const fetchPage = useCallback((sinceId, append) => {
    if (!bridgeRef.current) {
      bridgeRef.current = new BridgeClient();
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    bridgeRef.current
      .getDiagnosticsEvents({ since_id: sinceId, severity_min: severityMin, scopes, limit })
      .then((response) => {
        if (cancelled) return;

        if (response.result === 'error') {
          setError(response.message || response.error || t('operator.diag.events.loadFailed'));
          setIsLoading(false);
          return;
        }

        const normalized = (response.events || []).map(normalizeEvent);
        setEvents((prev) => (append ? [...prev, ...normalized] : normalized));
        setHasMore(!!response.has_more);
        setNextSinceId(response.next_since_id ?? sinceId);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || err?.error || t('operator.diag.events.loadFailed'));
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [severityMin, scopes, limit, t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) {
      setEvents([]);
      setHasMore(false);
      setNextSinceId(0);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    return fetchPage(0, false);
  }, [enabled, fetchPage]);

  const loadMore = useCallback((currentNextSinceId) => {
    if (!enabled || isLoading) return;
    fetchPage(currentNextSinceId, true);
  }, [enabled, isLoading, fetchPage]);

  const refresh = useCallback(() => {
    if (!enabled) return;
    setEvents([]);
    setNextSinceId(0);
    fetchPage(0, false);
  }, [enabled, fetchPage]);

  return {
    events,
    hasMore,
    nextSinceId,
    isLoading,
    error,
    loadMore,
    refresh,
  };
}
